// PQ event/tuning badge on the dashboard tile. Reads precomputed fields
// off the service object and renders the appearance variant produced by
// the pure `deriveBadgeAppearance` helper. No decision logic here.
//
// The fields it needs (flagged / reason / inominal_status / dominant_severity
// / events_last_hour) are LIVE — pushed via the dyno_snapshot WS, not present
// in the one-time /api/services fetch. Callers MUST pass the live service
// (from liveFeed), not the static metadata object, or the badge stays hidden.
//
// See plan: docs/plans/2026-05-15-feat-pq-tuning-dashboard-plan.md
// §"Visibility + styling".

import type { Service } from "@/lib/types";
import { deriveBadgeAppearance } from "@/lib/pqTuning";
import { usePqTuningStore } from "@/stores/pqTuningStore";

type PqBadgeService = Pick<
  Service,
  | "system_id"
  | "inominal_status"
  | "flagged"
  | "reason"
  | "dominant_severity"
  | "events_last_hour"
>;

export function PqEventBadge({
  service,
  onClick,
}: {
  service: PqBadgeService;
  onClick: () => void;
}) {
  // An opted-out meter never flags for drift. A recalibration in progress
  // (`syncing`) is operator-initiated, not a drift signal, so it stays
  // visible regardless of opt-out.
  const optedOut = usePqTuningStore((s) =>
    s.flaggingOptOut.has(service.system_id),
  );
  const appearance = deriveBadgeAppearance(service);
  if (appearance.kind === "hidden") return null;
  if (optedOut && appearance.kind !== "syncing") return null;

  // Every non-hidden variant carries a radius.
  const diameter = appearance.radius * 2;
  const color =
    appearance.kind === "syncing" ? "#94a3b8" /* slate */ : appearance.color;
  // The upward (iNominal-too-low) and flood (too-many-events) badges pulse to
  // demand attention — both are actionable config warnings, not passive
  // event noise.
  const actionable =
    appearance.kind === "upward" || appearance.kind === "flood";
  const pulseClass =
    actionable
      ? "animate-pq-badge-pulse"
      : appearance.kind === "noise" && appearance.pulse === "fast"
      ? "animate-pq-badge-pulse-fast"
      : appearance.kind === "noise" && appearance.pulse === "normal"
      ? "animate-pq-badge-pulse"
      : "";
  const ariaLabel =
    appearance.kind === "syncing"
      ? `Recalibration in progress for ${service.system_id}`
      : appearance.kind === "upward"
      ? `iNominal too low for ${service.system_id} — retune recommended`
      : appearance.kind === "flood"
      ? `PQ event flood for ${service.system_id} — review iNominal`
      : `PQ tuning for ${service.system_id}`;

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`shrink-0 rounded-full cursor-pointer transition-transform hover:scale-110 ${pulseClass}`}
      style={{
        width: `${diameter}px`,
        height: `${diameter}px`,
        backgroundColor: color,
        // Halo ring: slate for syncing, amber for the actionable warnings
        // (upward / flood) so they read as an alert; a hairline otherwise.
        boxShadow:
          appearance.kind === "syncing"
            ? "0 0 0 2px rgba(148, 163, 184, 0.3)"
            : actionable
            ? "0 0 0 3px rgba(251, 146, 60, 0.35)"
            : "0 0 0 1px rgba(0, 0, 0, 0.2)",
        // Border-only spinner halo for syncing
        border: appearance.kind === "syncing" ? "1px dashed #cbd5e1" : "none",
      }}
    />
  );
}
